import type { IngestProject } from '../../../../domain/src/lib/ingest/ingest-project.model';

function parseBooleanFlag(value: unknown): boolean | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'si', 'sí', 'activo', 'active'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'inactivo', 'inactive'].includes(normalized)) {
      return false;
    }
  }
  return undefined;
}

/** Resuelve `is_active` desde variantes habituales del API de ingesta. */
export function resolveIngestProjectIsActive(raw: Record<string, unknown>): boolean {
  const directCandidates = [
    raw['is_active'],
    raw['active'],
    raw['isActive'],
    raw['enabled'],
  ];
  for (const candidate of directCandidates) {
    const parsed = parseBooleanFlag(candidate);
    if (parsed !== undefined) return parsed;
  }

  const statusRaw = raw['status'] ?? raw['state'];
  if (typeof statusRaw === 'string') {
    const status = statusRaw.trim().toLowerCase();
    if (['inactive', 'inactivo', 'disabled', 'deleted'].includes(status)) return false;
    if (['active', 'activo', 'enabled'].includes(status)) return true;
  }

  return true;
}

export function normalizeIngestProject(raw: unknown): IngestProject | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r['id']);
  const name = typeof r['name'] === 'string' ? r['name'] : '';
  if (!Number.isFinite(id) || !name.trim()) return null;

  const folderPathRaw = r['folder_path'];
  const folder_path =
    typeof folderPathRaw === 'string' && folderPathRaw.trim() ? folderPathRaw : name;
  const descriptionRaw = r['description'];
  const description = typeof descriptionRaw === 'string' ? descriptionRaw : null;

  return {
    id,
    name,
    description,
    folder_path,
    is_active: resolveIngestProjectIsActive(r),
  };
}

export function normalizeIngestProjectList(raw: unknown): IngestProject[] {
  const normalizeArray = (arr: unknown[]): IngestProject[] =>
    arr
      .map((item) => normalizeIngestProject(item))
      .filter((p): p is IngestProject => !!p);

  if (Array.isArray(raw)) {
    return normalizeArray(raw);
  }
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o['projects'])) {
      return normalizeArray(o['projects']);
    }
    if (Array.isArray(o['items'])) {
      return normalizeArray(o['items']);
    }
  }
  return [];
}
